/**
 *
 * DocumentSplitter
 *
 */

import React, { forwardRef, memo, useRef, useState, useCallback, useEffect } from 'react';
import { VariableSizeList as List, ListChildComponentProps, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useSafeState } from '@domonda/react-plumb';
import { ResolveOnTrigger } from 'lib/ResolveOnTrigger';
import { FormattedMessage } from 'lib/intl';

// api
import { Invoice } from 'lib/api/invoice';
import { InvoiceForSplitting, InvoiceForSplittingWithSrc } from 'lib/api/invoiceContainer';

// ui
import { Grid, Box, Flex, Dialog, Err, ErrInline, Text, Button, IconButton } from '@domonda/ui';

// icons
import { ZoomInDocument, ZoomOutDocument, UnfoldLessIcon } from 'lib/domonda-icons';

// utils
import {
  deriveInvoicesForSplitting,
  togglePageDeleted,
  splitAfter,
  joinAfter,
  getItemSize,
  getEstimatedItemSize,
} from './utils';

// parts
import { DocumentSplitterInvoice } from './DocumentSplitterInvoice';

// decorate
import { decorate, Decorate } from './decorate';
import { CircularLoading } from 'modules/Loading/CircularLoading';

interface ListInnerProps {
  children: React.ReactNode;
  style: React.CSSProperties;
}

export interface ListColumnData {
  classes: Decorate['classes'];
  zoom: number;
  invoices: InvoiceForSplittingWithSrc[];
  doDeletePage: (invoiceIndex: number, pageIndex: number) => void;
  doSplitAfter: (invoiceIndex: number, pageIndex: number) => void;
  doJoinAfter: (invoiceIndex: number) => void;
}

interface ListColumnProps extends ListChildComponentProps {
  data: ListColumnData;
}

export interface DocumentSplitterProps {
  invoices: Invoice[];
  onDeleteContainer: () => Promise<void>;
  onCancelSplit: () => void;
  onApplySplit: (invoices: InvoiceForSplitting[]) => Promise<void>;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const LIST_PADDING = 15;

const ListInner = forwardRef<HTMLDivElement, ListInnerProps>(function ListInner(
  { style, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      style={Object.assign(
        {},
        style,
        { position: 'relative' },
        // Horizontal list must have list padding added to it's width
        // to properly show padding at the end of the list.
        style.width && typeof style.width === 'number' ? { width: style.width + LIST_PADDING } : {},
      )}
      {...rest}
    />
  );
});

if (process.env.NODE_ENV !== 'production') ListInner.displayName = 'ListInner';

const ListColumn = memo<ListColumnProps>(function ListColumn({ data, index, style }) {
  const { classes, zoom, invoices, doDeletePage, doSplitAfter, doJoinAfter } = data;
  const invoiceIndex = Math.floor(index / 2);
  const invoice = invoices[invoiceIndex];

  if (index % 2) {
    return (
      <Flex container align="center" style={style}>
        <Flex item className={classes.invoiceJoin}>
          <IconButton color="primary" variant="outlined" onClick={() => doJoinAfter(invoiceIndex)}>
            <UnfoldLessIcon style={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </Flex>
      </Flex>
    );
  } else {
    return (
      <DocumentSplitterInvoice
        style={style}
        zoom={zoom}
        invoice={invoice}
        invoiceIndex={invoiceIndex}
        doDeletePage={doDeletePage}
        doSplitAfter={doSplitAfter}
      />
    );
  }
}, areEqual);

if (process.env.NODE_ENV !== 'production') ListColumn.displayName = 'ListColumn';

const DocumentSplitter: React.FC<DocumentSplitterProps & Decorate> = (props) => {
  const { classes, invoices, onDeleteContainer, onCancelSplit, onApplySplit } = props;

  const listRef = useRef<List | null>(null);
  const [invoicesForSplitting, setInvoicesForSplitting] = useState(
    deriveInvoicesForSplitting(invoices),
  );
  const [zoom, setZoom] = useState(MIN_ZOOM);

  const [deleteDialog, setDeleteDialog] = useSafeState(false);

  useEffect(() => {
    // Must trigger re-render of the list
    // to make it recalculate item sizes after zoom value changes.
    if (listRef.current) {
      (listRef.current as any)._instanceProps.estimatedItemSize = getEstimatedItemSize(
        'horizontal',
        zoom,
      );
      listRef.current.resetAfterIndex(0, true);
    }
  }, [zoom]);

  const doDeletePage = useCallback(
    (invoiceIndex: number, pageIndex: number) =>
      setInvoicesForSplitting(togglePageDeleted(invoicesForSplitting, invoiceIndex, pageIndex)),
    [invoicesForSplitting],
  );

  const doSplitAfter = useCallback(
    (invoiceIndex: number, pageIndex: number) =>
      setInvoicesForSplitting(splitAfter(invoicesForSplitting, invoiceIndex, pageIndex)),
    [invoicesForSplitting],
  );

  const doJoinAfter = useCallback(
    (invoiceIndex: number) =>
      setInvoicesForSplitting(joinAfter(invoicesForSplitting, invoiceIndex)),
    [invoicesForSplitting],
  );

  const itemData: ListColumnData = {
    classes,
    zoom,
    invoices: invoicesForSplitting,
    doDeletePage,
    doSplitAfter,
    doJoinAfter,
  };

  return (
    <>
      <Dialog open={deleteDialog}>
        <Box padding={2}>
          <Flex container direction="column" align="center" spacing={1}>
            <ResolveOnTrigger
              promise={onDeleteContainer}
              params={{}}
              onResolve={() => setDeleteDialog(false)}
            >
              {({ trigger, loading, error, clearError }) =>
                loading ? (
                  <Flex item>
                    <CircularLoading />
                  </Flex>
                ) : (
                  <>
                    {error && (
                      <Flex item>
                        <ErrInline error={error} onClose={clearError} />
                      </Flex>
                    )}

                    <Flex item>
                      <Text>
                        <FormattedMessage id="DOCUMENT_DETAIL_DELETE_DIALOG_TITLE" />
                      </Text>
                    </Flex>

                    <Flex item container justify="flex-end" spacing={1}>
                      <Flex item>
                        <Button onClick={() => setDeleteDialog(false)}>
                          <FormattedMessage id="CANCEL" />
                        </Button>
                      </Flex>

                      <Flex item>
                        <Button color="error" onClick={trigger}>
                          <FormattedMessage id="DELETE" />
                        </Button>
                      </Flex>
                    </Flex>
                  </>
                )
              }
            </ResolveOnTrigger>
          </Flex>
        </Box>
      </Dialog>

      <ResolveOnTrigger promise={onApplySplit} params={invoicesForSplitting}>
        {({ trigger, loading, error, clearError }) => {
          if (loading)
            return (
              <Flex container direction="column" align="center" justify="center">
                <Flex item>
                  <CircularLoading />
                </Flex>
              </Flex>
            );

          if (error)
            return (
              <Flex container direction="column" align="center" justify="center">
                <Flex item>
                  <Err error={error} onRetry={clearError} />
                </Flex>
              </Flex>
            );

          return (
            <Grid
              fill
              container
              template={`
              'actionBar' 72px
              'content' calc(100% - 72px)
              / 100%`}
            >
              <Grid area="actionBar" className={classes.actionBar}>
                <Flex container justify="flex-end" align="center" spacing={1}>
                  <Flex item>
                    <IconButton
                      disabled={zoom <= MIN_ZOOM}
                      onClick={() => setZoom((currZoom) => Math.max(MIN_ZOOM, currZoom - 0.5))}
                    >
                      <ZoomOutDocument />
                    </IconButton>
                  </Flex>

                  <Flex item>
                    <IconButton
                      disabled={zoom >= MAX_ZOOM}
                      onClick={() => setZoom((currZoom) => Math.min(MAX_ZOOM, currZoom + 0.5))}
                    >
                      <ZoomInDocument />
                    </IconButton>
                  </Flex>

                  <Flex item flex={1} />

                  <Flex item>
                    <Button color="error" onClick={() => setDeleteDialog(true)}>
                      <FormattedMessage id="DELETE" />
                    </Button>
                  </Flex>

                  <Flex item>
                    <Button onClick={onCancelSplit}>
                      <FormattedMessage id="CANCEL" />
                    </Button>
                  </Flex>

                  <Flex item>
                    <Button color="secondary" variant="contained" onClick={trigger}>
                      <FormattedMessage id="APPLY" />
                    </Button>
                  </Flex>
                </Flex>
              </Grid>

              <Grid area="content" overflowing className={classes.content}>
                <Flex container>
                  <Flex item flex={1}>
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          ref={listRef}
                          width={width}
                          height={height}
                          layout="horizontal"
                          innerElementType={ListInner}
                          itemData={itemData}
                          itemCount={invoicesForSplitting.length * 2 - 1}
                          itemSize={(index) => getItemSize('horizontal', index, zoom)}
                          estimatedItemSize={getEstimatedItemSize('horizontal', zoom)}
                          style={{ padding: LIST_PADDING }}
                        >
                          {ListColumn}
                        </List>
                      )}
                    </AutoSizer>
                  </Flex>
                </Flex>
              </Grid>
            </Grid>
          );
        }}
      </ResolveOnTrigger>
    </>
  );
};

const ComposedDocumentSplitter = decorate(DocumentSplitter);
export { ComposedDocumentSplitter as DocumentSplitter };
